import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageChoice, ThemeChoice, ThemeMenu } from '@easydent/shared-ui';
import LanguageToggle from '@/components/LanguageToggle';
import { setLang } from '@/i18n';
import { clinicSignInUrl, patientSignInUrl } from '@/lib/apps';

// ── Brand mark ────────────────────────────────────────────────────────────────
/** The easyDent app icon: a teal-gradient squircle with a white tooth and an amber sparkle. */
export const Logo: React.FC<{ className?: string }> = ({ className }) => {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`edg-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#22B98A" />
          <stop offset="1" stopColor="#0B5340" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="16" fill={`url(#edg-${id})`} />
      {/* soft top highlight for depth */}
      <rect x="4" y="4" width="56" height="28" rx="13" fill="#ffffff" opacity="0.08" />
      <path fill="#ffffff" d="M32 14c-6 0-9 3-14 3-4 0-7-2-7 4 0 7 3 12 5 19 2 6 3 12 6 12 3 0 3-9 5-9s2 9 5 9c3 0 4-6 6-12 2-7 5-12 5-19 0-6-3-4-7-4-5 0-8-3-14-3z" />
      {/* amber sparkle accent */}
      <path fill="#F6A821" d="M46 10.5l1.7 3.8 3.8 1.7-3.8 1.7L46 21.5l-1.7-3.8-3.8-1.7 3.8-1.7z" />
    </svg>
  );
};

/** Backwards-compatible plain tooth glyph (single colour). */
export const Tooth: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden>
    <path fill="currentColor" d="M32 13c-6 0-9 3-14 3-4 0-7-2-7 4 0 7 3 12 5 19 2 6 3 12 6 12 3 0 3-9 5-9s2 9 5 9c3 0 4-6 6-12 2-7 5-12 5-19 0-6-3-4-7-4-5 0-8-3-14-3z" />
  </svg>
);

export const Wordmark: React.FC = () => (
  <Link to="/" className="flex items-center gap-2">
    <Logo className="h-9 w-9 drop-shadow-sm" />
    <span className="text-lg font-bold tracking-tight text-body">easyDent</span>
  </Link>
);

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
export const Icons = {
  search: <svg viewBox="0 0 24 24" {...stroke}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>,
  check: <svg viewBox="0 0 24 24" {...stroke}><path d="M20 6L9 17l-5-5" /></svg>,
  calendar: <svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
  bell: <svg viewBox="0 0 24 24" {...stroke}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>,
  folder: <svg viewBox="0 0 24 24" {...stroke}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>,
  shield: <svg viewBox="0 0 24 24" {...stroke}><path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" /><path d="M9 12l2 2 4-4" /></svg>,
  card: <svg viewBox="0 0 24 24" {...stroke}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>,
  chat: <svg viewBox="0 0 24 24" {...stroke}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
  spark: <svg viewBox="0 0 24 24" {...stroke}><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></svg>,
  pin: <svg viewBox="0 0 24 24" {...stroke}><path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>,
};

// ── Top navigation ──────────────────────────────────────────────────────────────
export const NavBar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const navLink = 'text-sm font-medium text-muted-strong hover:text-body';
  // Right padding keeps header content clear of the fixed RO/EN language toggle (top-right).
  return (
    <header className="sticky top-0 z-40 border-b border-line-soft bg-surface/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <Wordmark />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-5 sm:flex">
          <Link to="/directory" className={navLink}>{t('home.nav.findDentist')}</Link>
          <Link to="/for-clinics" className={navLink}>{t('home.nav.forClinics')}</Link>
          <Link to="/features" className={navLink}>{t('home.nav.features', 'Features')}</Link>
          <LanguageToggle />
          <ThemeMenu labels={{
            label: t('common.theme.label', 'Theme'), light: t('common.theme.light', 'Light'),
            dark: t('common.theme.dark', 'Dark'), system: t('common.theme.system', 'System'),
          }} />
          <details className="relative">
            <summary className="flex cursor-pointer list-none items-center gap-1 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 [&::-webkit-details-marker]:hidden">
              {t('home.nav.signIn')}
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" {...stroke}><path d="M6 9l6 6 6-6" /></svg>
            </summary>
            <div className="absolute right-0 mt-2 w-44 overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-lg">
              <a href={clinicSignInUrl()} className="block px-4 py-2 text-sm text-body-soft hover:bg-canvas">{t('home.nav.staff')}</a>
              <a href={patientSignInUrl()} className="block px-4 py-2 text-sm text-body-soft hover:bg-canvas">{t('home.nav.patient')}</a>
            </div>
          </details>
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={t('home.nav.menu', 'Menu')}
          aria-expanded={open}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-body-soft hover:bg-subtle sm:hidden"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" {...stroke}>
            {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav className="border-t border-line-soft bg-surface px-4 py-2 sm:hidden">
          <Link to="/directory" onClick={() => setOpen(false)} className="block rounded-lg px-2 py-2.5 text-sm font-medium text-body-soft hover:bg-canvas">{t('home.nav.findDentist')}</Link>
          <Link to="/for-clinics" onClick={() => setOpen(false)} className="block rounded-lg px-2 py-2.5 text-sm font-medium text-body-soft hover:bg-canvas">{t('home.nav.forClinics')}</Link>
          <Link to="/features" onClick={() => setOpen(false)} className="block rounded-lg px-2 py-2.5 text-sm font-medium text-body-soft hover:bg-canvas">{t('home.nav.features', 'Features')}</Link>
          <div className="my-1 border-t border-line-soft" />
          <a href={clinicSignInUrl()} className="block rounded-lg px-2 py-2.5 text-sm font-medium text-brand-ink-strong hover:bg-brand-50">{t('home.nav.staff')}</a>
          <a href={patientSignInUrl()} className="block rounded-lg px-2 py-2.5 text-sm font-medium text-brand-ink-strong hover:bg-brand-50">{t('home.nav.patient')}</a>
          {/* Display preferences belong in the menu on a phone. They used to float in the corner
              on top of the hamburger — at 375px the globe icon covered the button's centre, so a
              tap on "menu" opened the language list instead. */}
          <div className="my-1 border-t border-line-soft" />
          <ThemeChoice labels={{
            label: t('common.theme.label', 'Theme'), light: t('common.theme.light', 'Light'),
            dark: t('common.theme.dark', 'Dark'), system: t('common.theme.system', 'Auto'),
          }} />
          <LanguageChoice
            value={i18n.language}
            languages={[{ code: 'ro', label: 'Română' }, { code: 'en', label: 'English' }]}
            onChange={(c) => setLang(c === 'en' ? 'en' : 'ro')}
            label={t('common.language', 'Language')}
          />
        </nav>
      )}
    </header>
  );
};

// ── Footer ──────────────────────────────────────────────────────────────────────
export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const col = 'text-sm text-muted hover:text-body';
  return (
    <footer className="border-t border-line-soft bg-surface">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <Wordmark />
          <p className="mt-3 max-w-xs text-sm text-muted">{t('home.footer.tagline')}</p>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-subtle-ink">{t('home.footer.patients')}</h3>
          <ul className="mt-3 space-y-2">
            <li><Link to="/directory" className={col}>{t('home.footer.findDentist')}</Link></li>
            <li><a href={patientSignInUrl()} className={col}>{t('home.footer.patientSignin')}</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-subtle-ink">{t('home.footer.clinics')}</h3>
          <ul className="mt-3 space-y-2">
            <li><Link to="/for-clinics" className={col}>{t('home.footer.forClinics')}</Link></li>
            <li><Link to="/features" className={col}>{t('home.footer.features', 'All features')}</Link></li>
            <li><Link to="/features/scheduling" className={col}>{t('features.scheduling.title', 'Scheduling')}</Link></li>
            <li><Link to="/features/patient-records" className={col}>{t('features.patient-records.title', 'Patient records')}</Link></li>
            <li><Link to="/features/invoicing" className={col}>{t('features.invoicing.title', 'Invoicing')}</Link></li>
            <li><Link to="/for-clinics" className={col}>{t('home.footer.freeTrial')}</Link></li>
            <li><a href={clinicSignInUrl()} className={col}>{t('home.footer.clinicSignin')}</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-subtle-ink">{t('home.footer.account')}</h3>
          <ul className="mt-3 space-y-2">
            <li><a href={clinicSignInUrl()} className={col}>{t('home.nav.staff')}</a></li>
            <li><a href={patientSignInUrl()} className={col}>{t('home.nav.patient')}</a></li>
            {/* The only route to a person for someone who has no account to sign into. */}
            <li><Link to="/contact" className={col}>{t('contact.title')}</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-subtle-ink">{t('home.footer.legal', 'Legal')}</h3>
          <ul className="mt-3 space-y-2">
            <li><Link to="/legal/terms" className={col}>{t('legal.doc.terms', 'Terms of service')}</Link></li>
            <li><Link to="/legal/privacy" className={col}>{t('legal.doc.privacy', 'Privacy policy')}</Link></li>
            <li><Link to="/legal/cookies" className={col}>{t('legal.doc.cookies', 'Cookie policy')}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-line-soft py-4 text-center text-xs text-subtle-ink">{t('home.footer.rights')}</div>
    </footer>
  );
};
